/**
 * Certificate Flow Event Tracking
 * Tracks user journey through certificate generation and sharing
 */

import { posthog } from '../posthog';
import type {
  CertificateModalOpenedProperties,
  CertificateRequestedProperties,
  CertificateGeneratedProperties,
  CertificateGenerationFailedProperties,
  CertificateDownloadedProperties,
  CertificateSharedProperties,
  CertificateVerifiedProperties,
} from '../types';

// Store certificate request times for calculating generation duration
const certificateRequestTimes = new Map<string, number>();
// Store certificate generation times for calculating time since generation
const certificateGenerationTimes = new Map<string, number>();

export const certificateEvents = {
  /**
   * Track when certificate modal is opened
   * Trigger: User opens certificate generation modal
   */
  modalOpened: (props: Omit<CertificateModalOpenedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('certificate_modal_opened', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track when user requests certificate generation
   * Trigger: User clicks "Generate Certificate" button
   */
  requested: (props: Omit<CertificateRequestedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    certificateRequestTimes.set(props.enrollment_id, timestamp);

    posthog.capture('certificate_requested', {
      ...props,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track successful certificate generation
   * Trigger: PDF generation completes successfully
   */
  generated: (props: Omit<CertificateGeneratedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const requestTime = certificateRequestTimes.get(props.certificate_id);
    const generationTime = requestTime ? timestamp - requestTime : props.generation_time_ms;

    // Store generation time for download tracking
    certificateGenerationTimes.set(props.certificate_id, timestamp);

    // Clean up request time
    certificateRequestTimes.delete(props.certificate_id);

    posthog.capture('certificate_generated', {
      ...props,
      generation_time_ms: generationTime,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track certificate generation failure
   * Trigger: PDF generation fails or times out
   */
  generationFailed: (props: Omit<CertificateGenerationFailedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const requestTime = certificateRequestTimes.get(props.content_id);
    const generationTime = requestTime ? timestamp - requestTime : props.generation_time_ms;

    posthog.capture('certificate_generation_failed', {
      ...props,
      generation_time_ms: generationTime,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track certificate download
   * Trigger: User clicks download button
   */
  downloaded: (props: Omit<CertificateDownloadedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const generationTime = certificateGenerationTimes.get(props.certificate_id);
    const timeSinceGeneration = generationTime
      ? timestamp - generationTime
      : props.time_since_generation_ms;

    posthog.capture('certificate_downloaded', {
      ...props,
      time_since_generation_ms: timeSinceGeneration,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track certificate sharing
   * Trigger: User shares certificate link
   */
  shared: (props: Omit<CertificateSharedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('certificate_shared', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track certificate verification page access
   * Trigger: Public verification page is accessed
   */
  verified: (props: Omit<CertificateVerifiedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('certificate_verified', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Clear stored times for a certificate
   */
  clearTimes: (certificateId: string) => {
    certificateRequestTimes.delete(certificateId);
    certificateGenerationTimes.delete(certificateId);
  },
};
